if (!customElements.get('wlsn-buybox-gallery')) {
  class WLSNBuyboxGallery extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        active: 'active',
        hidden: 'hidden',
        disabled: 'disabled',
        mainSlider: 'js-buybox-gallery-main-slider',
        thumbSlider: 'js-buybox-gallery-thumb-slider',
        mainSlide: 'js-buybox-gallery-main-slide',
        thumbSlide: 'js-buybox-gallery-thumb-slide',
        thumbContainer: 'js-buybox-gallery-thumb-container',
        mainNavButtonPrev: 'js-buybox-gallery-main-nav-button-prev',
        mainNavButtonNext: 'js-buybox-gallery-main-nav-button-next',
      };

      this.bindMethods = {};

      this.mainSwiper = null;
      this.thumbSwiper = null;
      this.minThumbsForSlider = 2;

      this.rem = parseFloat(getComputedStyle(document.documentElement).fontSize);
    }

    /**
     * Get DOM elements
     * @returns {object} - Object containing DOM element references
     */
    get elements() {
      return {
        mainSlider: this.querySelector(`.${this.classes.mainSlider}`),
        thumbSlider: this.querySelector(`.${this.classes.thumbSlider}`),
        mainSlides: this.querySelectorAll(`.${this.classes.mainSlide}`),
        thumbSlides: this.querySelectorAll(`.${this.classes.thumbSlide}`),
        thumbContainer: this.querySelector(`.${this.classes.thumbContainer}`),
        mainNavButtonPrev: this.querySelector(`.${this.classes.mainNavButtonPrev}`),
        mainNavButtonNext: this.querySelector(`.${this.classes.mainNavButtonNext}`),
      };
    }

    /**
     * Connected callback lifecycle method
     * @returns {void}
     */
    connectedCallback() {
      this.hydrateOverrideThumbnails();
      this.initThumbSlider();
      this.initMainSlider();
    }

    /**
     * Builds thumbnail slides from the child blocks' rendered main images.
     * Nested Shopify blocks render their own markup and cannot be read reliably
     * as setting objects from a parent snippet.
     * @returns {void}
     */
    hydrateOverrideThumbnails() {
      if (this.dataset.hasGalleryOverrides !== 'true') return;

      const thumbWrapper = this.elements.thumbSlider?.querySelector('.swiper-wrapper');
      if (!thumbWrapper) return;

      this.elements.mainSlides.forEach((mainSlide, index) => {
        const sourceImage = mainSlide.querySelector('img');
        if (!sourceImage) return;

        const thumbSlide = document.createElement('div');
        thumbSlide.className = `swiper-slide wlsn-buybox-gallery__thumb-slide js-buybox-gallery-thumb-slide${index === 0 ? ' active' : ''}`;
        thumbSlide.dataset.slideIndex = String(index);
        thumbSlide.dataset.mediaType = 'image';

        const thumbWrapperInner = document.createElement('div');
        thumbWrapperInner.className = 'wlsn-buybox-gallery__thumb-wrapper';
        thumbWrapperInner.appendChild(sourceImage.cloneNode(true));
        thumbSlide.appendChild(thumbWrapperInner);
        thumbWrapper.appendChild(thumbSlide);
      });
    }
    
    /**
     * Disconnected callback lifecycle method
     * @returns {void}
     */
    disconnectedCallback() {
      if (this.mainSwiper) {
        this.mainSwiper.destroy(true, true);
      }
      if (this.thumbSwiper) {
        this.thumbSwiper.destroy(true, true);
      }
    }

    /**
     * Initialize the thumbnail slider
     * @returns {void}
     */
    initThumbSlider() {
      const { thumbContainer, thumbSlider, thumbSlides } = this.elements;
      if (!thumbContainer || !thumbSlider) return;

      // Check if we have enough thumbnails to show slider
      if (thumbSlides.length < this.minThumbsForSlider) {
        this.hideThumbSlider();
        return;
      }

      const thumbConfig = {
        loop: false,
        spaceBetween: (8 / 10) * this.rem,
        slidesPerView: 8,
        watchSlidesProgress: true,
        clickable: true,
        centerInsufficientSlides: true,
        breakpoints: {
          1024: {
            slidesPerView: 5,
          },
          1320: {
            slidesPerView: 6,
          },
          1440: {
            slidesPerView: 8,
          }
        }
      };

      this.thumbSwiper = new Swiper(thumbSlider, thumbConfig);
      
      // Check if we have insufficient slides for alignment
      // Add class if slides don't fill the viewport (less than slidesPerView) or if Swiper is locked
      if (thumbSlides.length < thumbConfig.slidesPerView || this.thumbSwiper.isLocked) {
        thumbSlider.classList.add('wlsn-buybox-gallery__thumb-slider--insufficient-slides');
      }
    }

    /**
     * Initialize the main slider
     * @returns {void}
     */
    initMainSlider() {
      const { mainSlider, mainSlides } = this.elements;
      if (!mainSlider) return;
      if (mainSlides.length < 2) return;

      const mainConfig = {
        loop: true,
        spaceBetween: (10 / 10) * this.rem,
        keyboard: {
          enabled: true,
        },
        grabCursor: true,
				slidesPerView: 1.01,
        centeredSlides: true,
        breakpoints: {
          768: {
            slidesPerView: 1,
            centeredSlides: false,
          }
        }
      };

      mainConfig.thumbs = {
        swiper: this.thumbSwiper,
      };
      mainConfig.navigation = {
        nextEl: this.elements.mainNavButtonNext,
        prevEl: this.elements.mainNavButtonPrev,
      };

      this.mainSwiper = new Swiper(mainSlider, mainConfig);
      this.attachVideoHandlers();
    }

    /**
     * Play video on the active slide and pause all others.
     * Uses Swiper's active slide element (not DOM index) so loop + centeredSlides stay in sync.
     * @returns {void}
     */
    attachVideoHandlers() {
      if (!this.mainSwiper) return;

      const playActiveSlideVideo = () => {
        this.mainSwiper.slides.forEach((slideEl) => {
          const video = slideEl.querySelector('video');
          if (video) video.pause();
        });

        const activeSlide = this.mainSwiper.slides[this.mainSwiper.activeIndex];
        const video = activeSlide?.querySelector('video');
        if (video) {
          video.play().catch(() => {});
        }
      };

      this.mainSwiper.on('slideChange', playActiveSlideVideo);
      playActiveSlideVideo();
    }
    
    /**
     * Hide the thumbnail slider
     * @returns {void}
     */
    hideThumbSlider() {
      const { thumbContainer } = this.elements;
      if (thumbContainer) {
        thumbContainer.classList.add(this.classes.hidden);
      }
    }
  }
  customElements.define('wlsn-buybox-gallery', WLSNBuyboxGallery);
}
